// src/pages/CheckoutPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';

const GEOAPIFY_KEY = process.env.REACT_APP_GEOAPIFY_KEY; // set in your .env

export default function CheckoutPage() {
  const { cart } = useCart();
  const [customer, setCustomer] = useState({
    name: '', phone: '', email: '',
    addressLine1: '', addressLine2: '',
    city: '', province: '', postalCode: '',
    coord: null // { lat, lon }
  });
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [query, setQuery] = useState('');
  const [estimate, setEstimate] = useState(null);
  const debounceRef = useRef(null);

  const total = cart.reduce((s, i) => s + i.subtotal, 0);
  const provinces = ['AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT'];

  // Debounced autocomplete for address
  useEffect(() => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(query)}&limit=6&filter=countrycode:ca&lang=en&apiKey=${GEOAPIFY_KEY}`;
        const res = await fetch(url);
        const data = await res.json();
        setSuggestions(data.features || []);
      } catch (err) {
        console.error('autocomplete error', err);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const handleChange = e => {
    const { name, value } = e.target;
    setCustomer(c => ({ ...c, [name]: value }));
  };

  // When user picks an autocomplete suggestion:
  const handlePickSuggestion = feat => {
    const prop = feat.properties || {};
    const geom = feat.geometry || {};
    setCustomer(c => ({
      ...c,
      addressLine1: prop.address_line || prop.formatted || c.addressLine1,
      city: prop.city || prop.county || prop.state || c.city,
      province: prop.state || c.province,
      postalCode: prop.postcode || c.postalCode,
      coord: { lat: geom.coordinates[1], lon: geom.coordinates[0] }
    }));
    setQuery(prop.formatted || '');
    setSuggestions([]);
    // Auto-estimate after pick:
    fetchEstimate({
      coord: { lat: geom.coordinates[1], lon: geom.coordinates[0] }
    });
  };

  // Build items payload for backend
  const buildItems = () =>
    cart.map(i => ({
      productId: i.productId._id,
      name: i.name,
      amount: Math.round(i.productId.price * 100), // cents
      quantity: i.quantity
    }));

  // Fetch estimate from backend (calculate distance, shipping, tax)
const fetchEstimate = async ({ coord = customer.coord } = {}) => {
  if (!coord) return;
  try {
    const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/checkout/estimate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: buildItems(),
        destination: coord
      })
    });

    const json = await res.json();
    if (!res.ok) {
      // server returned error payload like { error: '...' }
      console.error('estimate failed:', json);
      setEstimate(null);
      return;
    }

    // expected: { distance_km, shipping_total, tax, grand_total, ... }
    setEstimate(json);
  } catch (err) {
    console.error('estimate error', err);
    setEstimate(null);
  }
};


  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer, items: buildItems() })
      });
      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      console.error(err);
      alert('Error initiating payment');
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto p-6 bg-white rounded shadow">
      <div className="flex justify-between items-center mb-6">
        <div><h1 className="text-2xl font-bold mb-4">Checkout</h1></div>
        <div><Link to="/cartpage" className="text-currentnav hover:underline">← back to cart</Link></div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {['name','phone','email'].map(field => (
          <input
            key={field}
            name={field}
            type={field==='email'?'email':'text'}
            placeholder={field.charAt(0).toUpperCase()+field.slice(1)}
            value={customer[field]}
            onChange={handleChange}
            required
            className="w-full border p-2 rounded"
          />
        ))}

        <div>
          <input
            name="addressLine1"
            placeholder="Address Line 1 (start typing...)"
            value={query || customer.addressLine1}
            onChange={e => { setQuery(e.target.value); }}
            className="w-full border p-2 rounded"
            autoComplete="off"
            required
          />
          {suggestions.length > 0 && (
            <ul className="border rounded mt-1 bg-white max-h-48 overflow-auto">
              {suggestions.map(feat => (
                <li key={feat.properties.place_id}
                  className="p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handlePickSuggestion(feat)}>
                  {feat.properties.formatted}
                </li>
              ))}
            </ul>
          )}
        </div>

        <input
          name="addressLine2" placeholder="Address Line 2 (optional)"
          value={customer.addressLine2} onChange={handleChange}
          className="w-full border p-2 rounded"
        />
        <div className="flex gap-2">
          <input name="city" placeholder="City" value={customer.city}
            onChange={handleChange} required className="flex-1 border p-2 rounded" />
          <select name="province" value={customer.province} onChange={handleChange}
            required className="w-32 border p-2 rounded">
            <option value="">Province</option>
            {provinces.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <input name="postalCode" placeholder="Postal Code" value={customer.postalCode}
            onChange={handleChange} required className="w-32 border p-2 rounded" />
        </div>

        {/* estimate preview */}
<div className="text-right">
  <p className="font-semibold">Items total: ${total.toFixed(2)}</p>

  {estimate && typeof estimate.distance_km === 'number' ? (
    <>
      <p>Distance: {Number(estimate.distance_km).toFixed(2)} km</p>
      <p>Shipping: ${(estimate.shipping_total != null ? (estimate.shipping_total / 100).toFixed(2) : '0.00')}</p>
      <p>HST (13%): ${(estimate.tax != null ? (estimate.tax / 100).toFixed(2) : '0.00')}</p>
      <p className="font-semibold">Order Total: ${(estimate.grand_total != null ? (estimate.grand_total / 100).toFixed(2) : '0.00')}</p>
    </>
  ) : (
    <p className="text-sm text-gray-500">Choose an address for shipping estimate</p>
  )}
</div>


        <button type="submit" disabled={loading} className="w-full bg-currentnav text-white p-3 rounded font-bold">
          {loading ? 'Processing…' : 'Proceed to Payment'}
        </button>
      </form>
    </div>
  );
}
